import React, {useState} from 'react';
import {
  Button,
  ButtonProps,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {useCreateIntentSource} from '../../../hooks/api/intent';

interface CreateIntentSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateIntentSourceModal = ({
  isOpen,
  onClose,
}: CreateIntentSourceModalProps) => {
  const toast = useToast();
  const createIntentSource = useCreateIntentSource();
  const [label, setLabel] = useState('');

  const handleCreate = () => {
    if (!label.trim()) {
      toast({
        status: 'error',
        title: 'Label is required',
        duration: 2_000,
        isClosable: true,
      });
      return;
    }

    createIntentSource.mutate(
      {type: 'api', label: label.trim()},
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: 'Intent source created.',
            duration: 2_000,
            isClosable: true,
          });
          setLabel('');
          onClose();
        },
        onError: () => {
          // Error toast is handled by the API layer
        },
      }
    );
  };

  const handleClose = () => {
    setLabel('');
    onClose();
  };

  return (
    <Modal isCentered={true} isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontWeight="normal" pb="1" pt="6">
          Create Intent Source
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px="6" pt="6" pb="8">
          <Input
            placeholder="Enter label (e.g., 'Website Visits')"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreate();
              }
            }}
          />
        </ModalBody>
        <Divider />
        <ModalFooter justifyContent="start" gap="4">
          <Button
            colorScheme="kbuttonblue"
            onClick={handleCreate}
            isLoading={createIntentSource.isPending}
          >
            Create
          </Button>
          <Button color="kgray.300" onClick={handleClose} variant="ghost">
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const CreateIntentSourceButton = (props: ButtonProps) => {
  const {isOpen, onOpen, onClose} = useDisclosure();

  return (
    <>
      <Button
        fontWeight="500"
        fontSize="14"
        colorScheme="kbuttonblue"
        onClick={onOpen}
        {...props}
      >
        Create
      </Button>
      <CreateIntentSourceModal isOpen={isOpen} onClose={onClose} />
    </>
  );
};
